/**
 * PositionalShellView — the DOM-agnostic base both finder views share (D33).
 *
 * `ButtonColumnView` (horizontal track) and `DropdownView` (vertical combobox
 * stack) render genuinely different DOM + layout, but the *positional plan
 * mechanics* between them are identical: the current `plan`, the per-position
 * node map (`nodesByPosition`), the user-select callback wiring, and the
 * reconcile-then-render loop skeleton (`renderPlan`). Those live here once; each
 * subclass fills in the divergent DOM steps via the abstract hooks.
 *
 * This is the Template-Method seam, NOT a forced single layout: the base owns
 * the order of operations (reconcile shell count → per position: reset + atomic
 * populate), the subclass owns *what a shell is* (`shellCount`/`addShell`/
 * `removeShell`) and *how a shell renders* (`resetShell`/`populatePosition`).
 * The base holds NO level policy (ADR-0009/0010) — `plan[i]` is a level number
 * the subclass uses only for label lookup.
 *
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/spec.md §3.3
 * @see docs/decisions/0010-model-finder-modal-responsive-column-rendering.md
 */

import type { Level } from '../domain/levels';
import { type NodeId, type Position, parseNodeId } from '../domain/brand';
import type { Node } from '../data/wire';
import type { ModelFinderView } from './view';

export abstract class PositionalShellView implements ModelFinderView {
  abstract readonly root: HTMLElement;

  /** The current plan's level number per position (label lookup only). */
  protected plan: Level[] = [];
  /**
   * Rendered nodes per POSITION, keyed by NodeId — the source for `onUserSelect`.
   * The OUTER key is the position index (a plain `number` loop variable — purely
   * internal, never crosses a boundary, so unbranded per ADR-0011 §6/D3); the
   * INNER key is the catalog `NodeId`.
   */
  private readonly nodesByPosition = new Map<number, Map<NodeId, Node>>();
  /**
   * The content signature each position was LAST rendered with — the ordered
   * node-id list joined into a string, keyed by position. `renderPlan` skips a
   * position whose signature (and plan level) is unchanged, so an earlier column
   * is never torn down and rebuilt on a deeper selection (the "previous columns
   * lose their styling" defect). Dropped per position by `removeShell`/teardown so
   * a position that shrank away then re-grows the same ids is never falsely skipped.
   */
  private readonly contentSignatureByPosition = new Map<number, string>();
  private userSelectCb: ((position: Position, node: Node | null) => void) | null = null;

  // -- ModelFinderView (shared positional skeleton) -------------------------

  onUserSelect(cb: (position: Position, node: Node | null) => void): void {
    this.userSelectCb = cb;
  }

  /**
   * Reconcile the shells to the plan, then render each column atomically: reset
   * the reused shell's transient state and populate it from `itemsByPosition` in
   * the SAME pass (never a cleared-pending state — D14/AC #7). The subclass owns
   * the DOM of `resetShell`/`populatePosition`; the order is fixed here.
   *
   * A position whose content (the ordered node-id list) AND plan level are
   * unchanged from the previous render is a NO-OP: its shell is neither reset nor
   * re-populated, so its already-rendered buttons — and the controller's
   * `--selected` class on them — survive untouched. The controller always drives
   * the WHOLE plan on every selection (not just the changed tail), so without this
   * guard a deeper selection would needlessly destroy-and-rebuild every earlier
   * column, dropping their selected styling for a frame ("the previous columns
   * lose their styling"). The controller's later `select()` calls stay correct
   * either way — they idempotently re-assert the path selection.
   */
  renderPlan(plan: Level[], itemsByPosition: ReadonlyArray<Node[]>): void {
    this.reconcileShells(plan.length);
    this.plan = [...plan];

    for (let position = 0; position < plan.length; position++) {
      const items = itemsByPosition[position] ?? [];
      const signature = this.signatureFor(plan[position], items);
      if (this.contentSignatureByPosition.get(position) === signature) continue;

      this.resetShell(position);
      this.populatePosition(position, items);
      this.contentSignatureByPosition.set(position, signature);
    }

    this.afterRenderPlan();
  }

  /** The content fingerprint of a rendered position: its plan level + ordered node ids. */
  private signatureFor(level: Level | undefined, items: Node[]): string {
    return `${level}|${items.map((n) => n.id).join(',')}`;
  }

  // -- Abstract per-finder DOM / layout (subclass owns the shape) ------------

  /** The current number of live shells (so the base can compute the delta). */
  protected abstract shellCount(): number;
  /** Create one shell at the tail (clone the `<template>`, bind it). */
  protected abstract addShell(): void;
  /** Remove the tail shell (and tear down its position's widget state). */
  protected abstract removeShell(): void;
  /** Reset a reused shell's transient state for `position` (D14). */
  protected abstract resetShell(position: number): void;
  /** Atomically fill `position`'s shell from `items` (D14/AC #7). */
  protected abstract populatePosition(position: number, items: Node[]): void;

  /** Optional post-render hook (e.g. overflow affordance refresh). No-op default. */
  protected afterRenderPlan(): void {}

  abstract select(position: Position, id: NodeId): void;
  abstract scrollActiveIntoView(position: Position): void;
  abstract revealSelectionOnOpen(): void;
  abstract finalizePresentationAfterLayout(): void;
  abstract showCta(cta: Parameters<ModelFinderView['showCta']>[0]): void;
  abstract applyLabels(labels: Parameters<ModelFinderView['applyLabels']>[0]): void;
  abstract layout(): void;
  abstract destroy(): void;

  // -- Shared helpers the subclasses reuse ----------------------------------

  /** Add/remove real DOM shells at the tail so the count matches the plan (D10). */
  private reconcileShells(count: number): void {
    while (this.shellCount() < count) this.addShell();
    while (this.shellCount() > count) this.removeShell();
  }

  /**
   * Tear every CLONED shell (position ≥1) back down to the SSR baseline (only
   * position 0, the server-rendered seed, survives). The symmetric counterpart of
   * `reconcileShells` — both built on the same `removeShell()` hook (DRY), so each
   * removed position has its widget + node-map state torn down exactly as during a
   * shrinking render.
   *
   * The modal re-news the whole view per OPEN (model-finder-page-modal.ts), so a
   * fresh view is constructed on the SAME root DOM each time. Without this, the
   * previous cycle's cloned shells would linger in the track and the next
   * `reconcileShells` would APPEND on top of them (the columns-double-on-re-open
   * bug). Shell-creation ownership lives in the view, so shell-teardown does too
   * (SRP) — `destroy()` calls this. Idempotent: a baseline-only view is a no-op.
   */
  protected teardownShells(): void {
    while (this.shellCount() > 1) this.removeShell();
    this.plan = [];
    this.nodesByPosition.clear();
    // The surviving SSR seed (position 0) is re-bound by a fresh view on re-mount;
    // drop its signature too so the next first render always populates it.
    this.contentSignatureByPosition.clear();
  }

  /** The per-position node map (created on demand). Outer position key stays a plain index. */
  protected nodesAt(position: number): Map<NodeId, Node> {
    let map = this.nodesByPosition.get(position);
    if (!map) {
      map = new Map();
      this.nodesByPosition.set(position, map);
    }
    return map;
  }

  /** Drop a removed position's node map + content signature (called from `removeShell`). */
  protected dropNodesAt(position: number): void {
    this.nodesByPosition.delete(position);
    // Drop the signature too: a shrunk-away position that later re-grows the same
    // ids must re-populate, not be skipped as "unchanged" against a stale entry.
    this.contentSignatureByPosition.delete(position);
  }

  /** Hand a user selection (`node`, or `null` for a clear) to the controller. */
  protected emitSelect(position: Position, node: Node | null): void {
    this.userSelectCb?.(position, node);
  }

  /** Resolve a clicked/selected `<select>`-style value to its Node (or null = clear). */
  protected resolveSelectedNode(position: number, rawValue: string): Node | null {
    if (!rawValue) return null;
    // Parse the DOM-read id with the shared rule (number or 22-char public id) —
    // never `Number()`, which turns a public id into NaN. Unparsable → null.
    const id = parseNodeId(rawValue);
    return id === null ? null : (this.nodesAt(position).get(id) ?? null);
  }
}
